import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import ForgotDialog from "./ForgotDialog";

export default function AuthPanel({ onSuccess, idPrefix = "auth" }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tries, setTries] = useState(0);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Signed in — welcome back");
      onSuccess();
    } catch (err) {
      setError(formatApiError(err));
      setTries((t) => t + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.form
        key={tries}
        onSubmit={submit}
        animate={tries ? { x: [0, -10, 10, -6, 6, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="mt-2 space-y-4"
        data-testid={`${idPrefix}-login-form`}
      >
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-email`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">
            Email
          </Label>
          <Input
            id={`${idPrefix}-email`}
            data-testid={`${idPrefix}-email-input`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@garage.co.uk"
            className="bg-ink border-line"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-password`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">
            Password
          </Label>
          <Input
            id={`${idPrefix}-password`}
            data-testid={`${idPrefix}-password-input`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-ink border-line"
          />
        </div>
        {error && (
          <p data-testid={`${idPrefix}-login-error`} className="text-ember text-xs font-mono">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid={`${idPrefix}-login-submit`}
          disabled={busy || !email || !password}
          className="w-full rounded-full bg-ember py-3 font-display text-lg font-bold uppercase tracking-wider text-ink hover:bg-ember/90 disabled:opacity-40 transition"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
          <button
            type="button"
            data-testid={`${idPrefix}-forgot-link`}
            onClick={() => setForgotOpen(true)}
            className="text-mid hover:text-hi transition"
          >
            Forgot password?
          </button>
          <span className="text-low normal-case tracking-normal">Demo: owner@taxedandtested.app · pitcrew123</span>
        </div>
      </motion.form>
      <ForgotDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </>
  );
}
